import * as $ from 'jquery';
import { IDisposable, disposable, serialDisposable, IEventAggregator, disposableSubscription } from 'common/barrels/core';
import { Observable, Subject, AsyncSubject, BehaviorSubject } from 'common/barrels/rx';
import { li, ul, div, table, tbody, icon } from 'common/barrels/dom';
import { IResourceProvider } from 'common/resourceprovider';
import * as rxui from 'common/rxui/core';
import { Request } from 'common/request';

import { SortDescription } from 'runtime/data/sortdescription';
import { WorkTrayView } from 'runtime/data/worktrayview';
import { WorkTrayPage } from 'runtime/data/worktraypage';
import { WorkflowInstanceInfoPage } from 'runtime/data/workflowinstanceinfopage';
import { SearchResult } from 'runtime/data/searchresult';
import { SearchResultPage } from 'runtime/data/searchresultpage';
import { WorkItemInfo } from 'runtime/data/workiteminfo';
import { WorkflowInstanceInfo } from 'runtime/data/workflowinstanceinfo';
import { WorkTrayViewInfo } from 'runtime/data/worktrayviewinfo';
import { LookupItem } from 'runtime/data/lookupitem';
import { UserInfo } from 'runtime/data/userinfo';

import { Busy } from 'runtime/events/busy';
import { NotBusy } from 'runtime/events/notbusy';
import { ValidationFailed } from 'shared/data/validation';
import { DialogRequested } from 'runtime/events/dialogrequested';
import { ScreenLoadError } from 'runtime/events/screenloaderror';
import { RuntimeInfoRequested } from 'runtime/events/runtimeinforequested';

import { ViewEditDialog } from 'runtime/dialogs/vieweditdialog';
import { ConfirmWorkTrayViewDeletionDialog } from 'runtime/dialogs/confirmworktrayviewdeletiondialog';
import { WorkTrayViewNotFoundDialog } from 'runtime/dialogs/worktrayviewnotfounddialog';
import { WorkTrayViewNotFound } from 'runtime/messages/worktrayviewnotfound';

import { Screen } from './screen';

import { camel } from 'common/functions/string';

import * as path from 'shared/path';

import * as rxtable from 'common/rxui/rxtable';
import { format } from 'common/functions/string';

import { Map } from 'immutable';

import { ViewSaveRequested } from 'runtime/events/viewsaverequested';

import { Control } from 'runtime/controls/control';
import { NumericBox } from 'runtime/controls/numericbox';

import { dateTimeTransmissionFormat, longDateTimeFormat } from 'common/functions/date';

import * as moment from 'moment';

import { SystemError } from 'common/exceptions/systemerror';

import { Serializer } from 'common/serializer';
import { NotificationSummaryRequested } from 'runtime/events/notificationsummaryrequested';
import { NotificationSummaryDialog } from 'runtime/dialogs/notificationsummarydialog';
import { emptyGuid, newGuid } from 'common/functions/guid';

export enum ViewScreenType {
    WorkTray,
    Search,
    WorkflowStatus,
}

export enum ViewResultType {
    WorkTray,
    WorkTrayBulkAction,
    WorkflowStatus,
    SearchWorkflowStatus,
    SearchWorkItems,
    WorkflowStatusHistory,
    ViewSettings
}

export interface IViewLoadPageArgs {
    viewId: string;
    pageSize: number;
    pageIndex: number;
    sortDescriptions: SortDescription[];
}

export type OpenAction = 'none' | 'view' | 'edit';

export class PageChangeSource {
    constructor(public pageSize: number, public pageIndex: number){}
}

export class ViewColumn {
    constructor(public name: string, public displayName: string) {}
}

export class ViewScreen extends Screen implements IDisposable {
    protected firstPageIndex = 0;
    protected defaultPageSize = 30;

    protected autoClearance = 15;
    protected minColumnWidth = 10;

    protected view: BehaviorSubject<WorkTrayView>;
    protected sortDescriptions: BehaviorSubject<SortDescription[]>;

    // page load related subjects
    protected pageSize: BehaviorSubject<number>;

    // pager related subjects
    protected pageIndex: BehaviorSubject<number>;
    private totalItemCount: BehaviorSubject<number>;

    protected pageSizeSource: Observable<PageChangeSource>;
    protected pageIndexSource: Observable<PageChangeSource>;

    // page paint related subjects
    protected page: BehaviorSubject<WorkTrayPage|WorkflowInstanceInfoPage|SearchResultPage>;

    protected $viewTable: JQuery;

    protected tHeadDisposable = serialDisposable(null);
    private tableResizeDisposable = serialDisposable(null);

    private columnExcludesByViewResultType: Map<ViewResultType, string[]>;
    protected columnTitlesByName = new ColumnTitlesByName();

    protected stateMap: {
        'new': string;
        available: string;
        claimed: string;
        complete: string;
        discarded: string;
    };

    protected viewInitialized = false;
    protected viewUpdating = false;
    private viewUpdatingTracker = 0;
    protected viewAvailable: AsyncSubject<boolean>;

    protected viewId: Subject<string>;
    private viewMenuDisposable = serialDisposable(null);
    protected viewResultType: ViewResultType;

    protected viewScreenDisposable: IDisposable;

    protected bulkActionNone: string;

    // action related subjects
    protected bulkAction: BehaviorSubject<string>;

    protected columns: ViewColumn[];
    protected columnSizeInfos: IColumnSizeInfo[] = [];
    protected columnMetadata: rxtable.ColumnInfo[];

    protected notificationSummaryRequests = new Subject <{ id: string, title: string}>();

    protected subscriptions: IDisposable;

    constructor(
        $hostElement: JQuery,
        resourcesObservable: Observable<IResourceProvider>,
        parentEventAggregator: IEventAggregator,
        private viewScreenType: ViewScreenType,
        serializer: Serializer,
        csrfToken: () => string
    ) {
        super($hostElement, resourcesObservable, parentEventAggregator, serializer, csrfToken);

        const columnExcludesByViewResultType = Map<ViewResultType, string[]>().asMutable();
        columnExcludesByViewResultType.set(ViewResultType.WorkTray, ['Score', 'CreateUserDisplayName']);
        columnExcludesByViewResultType.set(ViewResultType.WorkTrayBulkAction, ['Score', 'CreateUserDisplayName']);
        columnExcludesByViewResultType.set(ViewResultType.WorkflowStatus, ['Score', 'ClaimUserDisplayName']);
        columnExcludesByViewResultType.set(ViewResultType.SearchWorkflowStatus, ['Score', 'Priority', 'DueDate', 'LastModifiedDate', 'ClaimUserDisplayName']);
        columnExcludesByViewResultType.set(ViewResultType.SearchWorkItems, ['CreateUserDisplayName']);
        this.columnExcludesByViewResultType = columnExcludesByViewResultType.asImmutable();

        this.viewId = new Subject<string>();
        this.view = new BehaviorSubject<WorkTrayView>(null);
        this.sortDescriptions = new BehaviorSubject<SortDescription[]>(null);

        this.pageSize = new BehaviorSubject(this.defaultPageSize);

        this.pageIndex = new BehaviorSubject(this.firstPageIndex - 1);
        this.totalItemCount = new BehaviorSubject(0);

        this.pageSizeSource = this.pageSize
            .distinctUntilChanged()
            .map(value => new PageChangeSource(value /*pageSize*/, this.firstPageIndex));

        this.pageIndexSource = this.pageIndex
            .distinctUntilChanged()
            .map(value => new PageChangeSource(null /*pageSize*/, value /*pageIndex*/));

        this.page = new BehaviorSubject<WorkTrayPage|WorkflowInstanceInfoPage|SearchResultPage>(null);

        // action related subjects
        this.bulkAction = new BehaviorSubject<string>(null);

        this.viewAvailable = new AsyncSubject<boolean>();

        var resourcesSubscription: IDisposable = null;
        resourcesSubscription = disposableSubscription(this.resourcesAvailable.subscribe(
            () => {
                var resources = this.resources;
                try {
                    const actionDictionary = Map<ViewResultType, string>().asMutable();
                    actionDictionary.set(ViewResultType.WorkTray, '');
                    actionDictionary.set(ViewResultType.WorkTrayBulkAction, '');
                    actionDictionary.set(ViewResultType.WorkflowStatus, '');
                    actionDictionary.set(ViewResultType.SearchWorkItems, '');
                    actionDictionary.set(ViewResultType.SearchWorkflowStatus, '');
                    actionDictionary.set(ViewResultType.ViewSettings, resources.getCoreString('XamlAction'));

                    const titleDictionary = Map<ViewResultType, string>().asMutable();
                    titleDictionary.set(ViewResultType.WorkTray, resources.getCoreString('XamlWorkItemName'));
                    titleDictionary.set(ViewResultType.WorkTrayBulkAction, resources.getCoreString('XamlWorkItemName'));
                    titleDictionary.set(ViewResultType.WorkflowStatus, resources.getCoreString('XamlWorkItemName'));
                    titleDictionary.set(ViewResultType.SearchWorkItems, resources.getCoreString('XamlWorkItemName'));
                    titleDictionary.set(ViewResultType.SearchWorkflowStatus, resources.getCoreString('XamlWorkItemName'));
                    titleDictionary.set(ViewResultType.ViewSettings, resources.getCoreString('XamlName'));

                    const stepTitleDictionary = Map<ViewResultType, string>().asMutable();
                    stepTitleDictionary.set(ViewResultType.WorkTray, resources.getCoreString('XamlStep'));
                    stepTitleDictionary.set(ViewResultType.WorkTrayBulkAction, resources.getCoreString('XamlStep'));
                    stepTitleDictionary.set(ViewResultType.WorkflowStatus, resources.getCoreString('XamlCurrentStep'));
                    stepTitleDictionary.set(ViewResultType.SearchWorkItems, resources.getCoreString('XamlStep'));
                    stepTitleDictionary.set(ViewResultType.SearchWorkflowStatus, resources.getCoreString('XamlCurrentStep'));
                    stepTitleDictionary.set(ViewResultType.ViewSettings, resources.getCoreString('XamlStep'));

                    const lastModifiedDateDictionary = Map<ViewResultType, string>().asMutable();
                    lastModifiedDateDictionary.set(ViewResultType.WorkTray, resources.getCoreString('XamlLastModified'));
                    lastModifiedDateDictionary.set(ViewResultType.WorkTrayBulkAction, resources.getCoreString('XamlLastModified'));
                    lastModifiedDateDictionary.set(ViewResultType.WorkflowStatus, resources.getCoreString('XamlLastModified'));
                    lastModifiedDateDictionary.set(ViewResultType.SearchWorkItems, resources.getCoreString('XamlLastModified'));
                    lastModifiedDateDictionary.set(ViewResultType.SearchWorkflowStatus, resources.getCoreString('XamlLastModified'));
                    lastModifiedDateDictionary.set(ViewResultType.ViewSettings, resources.getCoreString('XamlLastModifiedDate'));

                    this.columnTitlesByName.set('Process', ''), //, resources.getCoreString('XamlProcess'));
                    this.columnTitlesByName.set('Score', resources.getCoreString('XamlScore')); //: null,
                    this.columnTitlesByName.set('Action', actionDictionary);
                    this.columnTitlesByName.set('Title', titleDictionary);
                    this.columnTitlesByName.set('DefinitionTitle', resources.getCoreString('XamlType'));
                    this.columnTitlesByName.set('StepTitle', stepTitleDictionary);
                    this.columnTitlesByName.set('State', resources.getCoreString('XamlState'));
                    this.columnTitlesByName.set('ClaimUserDisplayName', resources.getCoreString('XamlClaimedBy'));
                    this.columnTitlesByName.set('Priority', resources.getCoreString('XamlPriority'));
                    this.columnTitlesByName.set('DueDate', resources.getCoreString('XamlDueDate'));
                    this.columnTitlesByName.set('LastModifiedDate', lastModifiedDateDictionary),
                    this.columnTitlesByName.set('ArrivalDate', resources.getCoreString('XamlArrivalDate'));
                    this.columnTitlesByName.set('CreateUserDisplayName', resources.getCoreString('XamlCreatedBy'));
                    this.columnTitlesByName.set('CreateDate', resources.getCoreString('XamlCreationDate'));
                    this.columnTitlesByName.set('SubmitUserDisplayName', resources.getCoreString('XamlSubmitUser'));
                    this.columnTitlesByName.set('SubmitDate', resources.getCoreString('XamlSubmitDate'));
                    this.columnTitlesByName.set('AttributeValue1', resources.getCoreString('XamlAttribute1'));
                    this.columnTitlesByName.set('AttributeValue2', resources.getCoreString('XamlAttribute2'));
                    this.columnTitlesByName.set('AttributeValue3', resources.getCoreString('XamlAttribute3'));
                    this.columnTitlesByName.set('AttributeValue4', resources.getCoreString('XamlAttribute4'));
                    this.columnTitlesByName.set('AttributeValue5', resources.getCoreString('XamlAttribute5'));
                    this.columnTitlesByName.set('AttributeValue6', resources.getCoreString('XamlAttribute6'));
                    this.columnTitlesByName.set('AttributeValue7', resources.getCoreString('XamlAttribute7'));
                    this.columnTitlesByName.set('AttributeValue8', resources.getCoreString('XamlAttribute8'));
                    this.columnTitlesByName.set('AttributeValue9', resources.getCoreString('XamlAttribute9'));
                    this.columnTitlesByName.set('AttributeValue10', resources.getCoreString('XamlAttribute10'));

                    this.stateMap = {
                        'new': resources.getCoreString('WorkItemStateNew'),
                        available: resources.getCoreString('WorkItemStateAvailable'),
                        claimed: resources.getCoreString('WorkItemStateClaimed'),
                        complete: resources.getCoreString('WorkItemStateComplete'),
                        discarded: resources.getCoreString('WorkItemStateDiscarded')
                    };
                } catch (exception) {
                    this.exception(exception);
                    return;
                }

                this.resources = resources;

                this.initializeView($hostElement, resources, resourcesSubscription);

                this.viewAvailable.next(true);
                this.viewAvailable.complete();
            },
            exception => this.exception(exception)));

        this.subscriptions = disposableSubscription(
            this.notificationSummaryRequests
                .switchMap(x => this.eventAggregator.request(new NotificationSummaryRequested(x.id)).map(y => ({ items: y, title: x.title })))
                .subscribe(x => {
                    this.eventAggregator.publish(new DialogRequested(new NotificationSummaryDialog(this.resources, x.items, x.title)));
                })
        );
    }

    dispose(): void {
        this.subscriptions.dispose();
    }

    protected updating($element?: JQuery): void {
        this.viewUpdatingTracker += 1;
        if (this.viewUpdatingTracker > 1) {
            return;
        }

        this.eventAggregator.publish(new Busy($element));
        this.viewUpdating = true;
    }

    protected notUpdating($element?: JQuery): void {
        if (this.viewUpdatingTracker > 0) { //ensure 0 is the lowest count
            this.viewUpdatingTracker -= 1;
        }

        if (this.viewUpdatingTracker === 0 && this.viewUpdating) {
            this.viewUpdating = false;
            this.eventAggregator.publish(new NotBusy($element));
        }
    }

    // this is a placeholder for the the actual view screens to implement
    protected onViewChanged(view: WorkTrayView, final: () => void): void { }

    protected paintViewHeader(view: WorkTrayView, viewResultType: ViewResultType): void {
        // clear any existing header elements and related subscriptions
        this.tHeadDisposable.set(null);

        // viewResultType will be cleared on view change, then
        // only set the column details when the viewResultType is not set or changes
        if (this.viewResultType === undefined || this.viewResultType === null || this.viewResultType !== viewResultType) {
            this.viewResultType = viewResultType;
            this.columns = this.determineViewColumnsInOrder(view, viewResultType);
        }

        // regenerate the column metadata for any local changes, e.g. column ordering or bulk action
        this.columnMetadata = this.getColumnInfos(this.columns, this.columnSizeInfos);

        var $table = $('.wf-view-table-inner > table', this.$element);

        // the table needs to be manually cleared here so that the content does not affect initial column sizing for the new data
        $table.children('tbody').empty();

        var $thead = rxtable.buildHeader($table, this.columnMetadata);

        this.tHeadDisposable.set(disposableSubscription(
            rxtable.makeHeadersDraggable($table).subscribe(value => this.moveColumn(value.oldIndex, value.newIndex)),
            rxtable.makeSortable($table).subscribe(value => this.sortColumn(value)),
            disposable(() => {
                this.tableResizeDisposable.set(null);
                $thead.remove();
            })
        ));

        this.applyColumnResizingLogic($table);

        rxtable.resize($table, this.columnMetadata, this.minColumnWidth, this.autoClearance);
    }

    // this is a placeholder for the the actual view screens to implement
    protected reloadPage(args: IViewLoadPageArgs, final: () => void): void { }

    // this is a placeholder for the the actual view screens to implement
    protected paintViewBodyVirtual(view: WorkTrayView, page: WorkTrayPage|WorkflowInstanceInfoPage|SearchResultPage): void {}

    // empties the grid and resets the pager when a request was rejected and there is no page to paint
    protected clearViewBody(): void {
        this.populateViewBody(0, 0, []);
    }

    private populateViewBody(pageIndex: number, totalItemCount: number, items: LookupItem[]): JQuery {
        this.updatePagerInfo(pageIndex, totalItemCount);

        var $table = $('.wf-view-table-inner > table', this.$element);
        rxtable.populateTable($table, this.columnMetadata, items, x => x.keyValue, null);
        rxtable.resize($table, this.columnMetadata, this.minColumnWidth, this.autoClearance);

        return $table;
    }

    protected paintViewBody(displayHistory: boolean, displayActionMenu: boolean, page: WorkTrayPage|WorkflowInstanceInfoPage|SearchResultPage): void {
        var $table = this.populateViewBody(page.pageIndex, page.totalItemCount, this.generateLookupItems(displayHistory, displayActionMenu, page.items));

        const runtimeInfo = new RuntimeInfoRequested();
        this.eventAggregator.publish(runtimeInfo);
        if (runtimeInfo.value.canViewNotificationSummary) {
            this.applyHoverActions($table.find('tbody'), 15, [
                this.notificationSummaryIconFactory(
                    k => k instanceof WorkItemInfo ? k.canView
                       : k instanceof WorkflowInstanceInfo ? true
                       : false,
                    k => k instanceof WorkItemInfo ? k.workItemId
                       : k instanceof WorkflowInstanceInfo ? k.workflowInstanceId
                       : null,
                    k => k instanceof WorkItemInfo ? k.title
                       : k instanceof WorkflowInstanceInfo ? k.title
                       : null,
                )
            ]);
        }
    }

    private notificationSummaryIconFactory(availabilitySelector: (k: any) => boolean, keySelector: (k: any) => string, titleSelector: (k: any) => string) {
        return (k: any) => availabilitySelector(k)
            ? icon('envelope').clicks(o => o.subscribe(() => {
                const key = k ? keySelector(k) : null;
                const title = k ? titleSelector(k) : null;
                if (k)
                    this.notificationSummaryRequests.next({ id: key, title: title });
            }))
            : $();
    }

    private applyHoverActions(
        $tbody: JQuery,
        horizontalAdjustment: number,
        iconFactories: ((key: any) => JQuery)[]
    ) {
        if (iconFactories.length < 1)
            return;

        $tbody.find('tr').on('mouseenter', e => {
            const $tr = $(e.target).closest('tr');

            const $td = $tr.find('td').eq(0);

            if ($tr.length > 0) {
                const right = horizontalAdjustment;

                const key = $tr.data('key');

                div('hoveritem')
                    .appendTo($td)
                    .css('top', $tr.position().top + $tr.height() - $tbody.parent().parent().scrollTop())
                    .css('right', `${right}px`)
                    .css('height', $tr.height())
                    .append(iconFactories.map(f => f(key)))
                    ;
            }
        });

        $tbody.find('tr').on('mouseleave', e => {
            $(e.target).closest('tr').find('.hoveritem').remove();
        });

        // when scrolling using the mousewheel, must adjust the position/top of the hoveritem
        $tbody.parent().parent().on('scroll', () => {
            const $hover = $tbody.find('.hoveritem');
            const $tr = $hover.closest('tr');

            // scroll seems to be triggered during row-specific updates, and in these cases, the tr will not be found; abort
            if ($tr.length === 0)
                return;

            $hover.css('top', $tr.position().top + $tr.height() - $tbody.parent().parent().scrollTop());
        });
    }

    protected insertHistoryTable($target: JQuery, columnMetadata: rxtable.ColumnInfo[], items: any[], offerHoverActions = true): void {
        const $tbody = tbody();

        const $table = table().append($tbody);
        rxtable.buildHeader($table, columnMetadata);
        rxtable.populateTable($table, columnMetadata, items, x => x.keyValue, null);

        if (offerHoverActions) {
            const runtimeInfo = new RuntimeInfoRequested();
            this.eventAggregator.publish(runtimeInfo);
            if (runtimeInfo.value.canViewNotificationSummary) {
                this.applyHoverActions($tbody, 15, [
                    this.notificationSummaryIconFactory(k => k.canView, k => k.workItemId, k => k.title)
                ]);
            }
        }

        var $history = $('<div />').addClass('rxui-table wf-history-table').append(
            $('<div />').append([
                $('<div />').addClass('rxui-table-header-background wf-history-table-header'),
                $('<div />').addClass('rxui-table-inner wf-history-table-inner').append(
                    $table
                )
            ])
        );

        var columnCount = $target[0].children.length;

        // insert a hidden row to maintain alternating row colours
        var $historyPlaceholderTr = $('<tr />').addClass('wf-item-history').addClass('rxui-table-nosize').css('display', 'none').insertAfter($target);
        // insert the actual history table row
        var $historyTr = $('<tr />').addClass('wf-item-history').addClass('rxui-table-nosize').append($('<td />').attr('colspan', columnCount).append($history)).insertAfter($historyPlaceholderTr);

        $target.on('remove', () => {
            $historyPlaceholderTr.remove();
            $historyTr.remove();
        });

        rxtable.resize($table, columnMetadata, this.minColumnWidth, this.autoClearance);

        var headerPositionSubscription = rxui.scrolled($table.parent()).throttleTime(1).subscribe(() => rxtable.updateHeadingPositions($table));
        $table.on('remove', () => headerPositionSubscription.unsubscribe());
    }

    private getColumnInfos(columns: ViewColumn[], columnSizeInfos: IColumnSizeInfo[]): rxtable.ColumnInfo[] {
        var defaultColumnSizeInfo = (column: ViewColumn): IColumnSizeInfo => {
            switch (column.name) {
                case 'Score':
                    return { name: camel(column.name), size: 80, sizeType: 'pixel' };
                case 'Process':
                    return { name: camel(column.name), size: 50/*75*/, sizeType: 'pixel' };
                case 'Title':
                    return { name: camel(column.name), size: 250, sizeType: 'pixel' };
                case 'DefinitionTitle':
                    return (this.viewResultType === ViewResultType.SearchWorkflowStatus || this.viewResultType === ViewResultType.SearchWorkItems)
                        ? { name: camel(column.name), size: 100, sizeType: 'pixel' }
                        : { name: camel(column.name), size: 130, sizeType: 'pixel' };
                case 'StepTitle':
                    return { name: camel(column.name), size: 120, sizeType: 'pixel' };
                default:
                    return { name: camel(column.name), size: 0, sizeType: 'auto' };
            }
        };

        const viewName = this.view.getValue().name;
        const defaultTitleRegex = /^Attribute (10|[1-9])$/;

        return columns.map(c => {
            var columnSizeInfo = columnSizeInfos.filter(cs => cs.name === c.name)[0] || defaultColumnSizeInfo(c);
            let columnHeader = this.columnTitlesByName.getTitle(c.name, this.viewResultType); //get default column title
            if (c.name.startsWith('AttributeValue')) { 
                // localization key is in the format {ViewName}.Attribute{N}
                const key = `${viewName}.${c.name.replace('Value', '')}`; 
                const localized = this.resources.getCoreString(key);
                if (localized) { // if the localized name is available, use it
                    columnHeader = localized;
                } else if (!defaultTitleRegex.test(c.displayName)) { // if the displayName is not a default attribute name, use it
                    columnHeader = c.displayName;
                }
            }
            return new rxtable.ColumnInfo(camel(c.name), columnHeader, columnSizeInfo.size, columnSizeInfo.sizeType);
        });        
    }

    private generateLookupItems(displayHistory: boolean, displayActionMenu: boolean, infos: (WorkItemInfo|WorkflowInstanceInfo|SearchResult)[]): LookupItem[] {
        var columnNames = this.columnMetadata.map(c => c.name);

        const runtimeInfo = new RuntimeInfoRequested();

        this.eventAggregator.publish(runtimeInfo);

        return infos.map(info => this.generateLookupItem(this.viewResultType, displayHistory, displayActionMenu, info, columnNames, runtimeInfo.value.userId));
    }

    protected generateLookupItem(viewResultType: ViewResultType, displayHistory: boolean, displayActionMenu: boolean, info: WorkItemInfo|WorkflowInstanceInfo|SearchResult, columnNames: string[], currentUserId: string): LookupItem {
        var getUserDisplayName = (userInfo: UserInfo): string => {
            return (userInfo !== undefined && userInfo !== null && userInfo.displayName !== undefined && userInfo.displayName !== null)
                ? userInfo.displayName
                : '';
        };

        const claimedByCurrentUser = (i: WorkItemInfo | SearchResult) => i.claimUserInfo && i.claimUserInfo.userId === currentUserId;

        const openAction = info instanceof (WorkflowInstanceInfo) ? 'none'
            : !info.canView ? 'none'
            : claimedByCurrentUser(info) ? 'edit'
            : 'view';

        return columnNames.reduce((p: any, cn) => {
            var columnName = camel(cn);
            var propName = columnName;

            if (viewResultType === ViewResultType.WorkflowStatusHistory && info instanceof WorkItemInfo) {
                if (columnName === 'state') {
                    columnName = 'stateEx';
                }
            } else if (info instanceof WorkflowInstanceInfo) {
                switch (columnName) {
                    case 'stepTitle':
                        columnName = 'stepTitle.workflowStatus';
                        break;
                    case 'lastModifiedDate':
                        columnName = 'stepTitle.lastModifiedDate';
                        break;
                }
            }

            switch (columnName) {
            case 'process':
                p[propName] = $('<input />').addClass('wf-item-process').attr('type', 'checkbox');
                break;
            case 'score':
                p[propName] = Math.round((<SearchResult>info).score * 1000000) / 1000000;
                break;
            case 'action':
                if (info instanceof WorkItemInfo || info instanceof SearchResult) {
                    //p[propName] = this.buildActionColumnContent(displayHistory, true, info.canEdit, info.canView, displayActionMenu);
                    p[propName] = this.buildActionColumnContent(displayHistory, true, openAction, displayActionMenu);
                } else {
                    p[propName] = this.buildActionColumnContent(true);
                }
                break;
            case 'stepTitle.workflowStatus':
                var items = (<WorkflowInstanceInfo>info).currentWorkItems;
                var comparer = (a: string, b: string) => a.localeCompare(b);
                var stepTitle = items.length > 0 ? items.map(x => x.stepTitle).filter((x, i, a) => a.indexOf(x) === i).sort(comparer).join(', ') : this.resources.getCoreString('LblNotApplicable');
                p[propName] = stepTitle;
                break;
            case 'stepTitle.lastModifiedDate':
                var items = (<WorkflowInstanceInfo>info).currentWorkItems;
                var lastModified = items.length > 0 ? this.toLongDateTimeFormat(items.map(x => x.lastModifiedDate).sort((a, b) => b.localeCompare(a))[0]) : this.resources.getCoreString('LblNotApplicable');
                p[propName] = lastModified;
                break;
            case 'state':
                if (info instanceof WorkflowInstanceInfo) {
                    p[propName] = this.resources.getCoreString(info.completed ? 'LblComplete' : 'LblInProgress');
                } else {
                    p[propName] = this.stateMap[info[propName]];
                }
                break;
            case 'stateEx':
                var workItemInfo = <WorkItemInfo>info;
                var state = (workItemInfo.state === 'claimed') ? format(this.resources.getCoreString('MsgClaimedBy'), workItemInfo.claimUserInfo.displayName)
                    : (workItemInfo.state === 'new') ? format(this.resources.getCoreString('MsgNewClaimedBy'), workItemInfo.claimUserInfo.displayName)
                    : this.stateMap[workItemInfo.state];
                p[propName] = state;
                break;
            case 'claimUserDisplayName':
                p[propName] = getUserDisplayName((<WorkItemInfo>info).claimUserInfo);
                break;
            case 'createUserDisplayName':
                p[propName] = getUserDisplayName((<WorkflowInstanceInfo>info).createUserInfo);
                break;
            case 'submitUserDisplayName':
                p[propName] = getUserDisplayName((<WorkItemInfo>info).submitUserInfo);
                break;
            case 'arrivalDate':
            case 'createDate':
            case 'dueDate':
            case 'lastModifiedDate':
            case 'submitDate':
                if (info[propName] === undefined) {
                    p[propName] = undefined;
                } else {
                    p[propName] = info[propName] ? this.toLongDateTimeFormat(info[propName]) : '';
                }
                break;
            default:
                if (info[propName] === undefined) {
                    p[propName] = undefined;
                } else {
                    p[propName] = info[propName] ? info[propName] : '';
                }
                break;
            }

            return p;
        }, { active: true, keyValue: info });
    }

    protected buildActionColumnContent(displayHistory: boolean, displayOpenAvailable: boolean = false, openAction: OpenAction = 'none', displayActionMenu: boolean = false): JQuery {
        var actionContent = ul('wf-item-actions');

        if (displayHistory) {
            $('<li />')
                .addClass('wf-item-menu-item wf-item-menu-item-history')
                .data('itemAction', 'showHistory')
                .append($('<i />').addClass('fa').addClass('fa-plus'))
                .appendTo(actionContent);
        }

        if (displayOpenAvailable) {
            const item = li('wf-item-menu-item')
                .text(this.resources.getCoreString('WorkItemScreenOpenWorkItemText'))
                .appendTo(actionContent)
                .data('itemAction', openAction === 'edit' ? 'edit' : 'view')
                ;

            if (openAction === 'none')
                item.addClass('ui-state-disabled');
        }

        if (displayActionMenu) {
            li('wf-item-menu-item wf-item-menu-item-menu').addClass('ui-menu-item').data('itemAction', 'showMenu').append($('<i />').addClass('fa').addClass('fa-caret-down')).appendTo(actionContent);
        }

        return actionContent;
    }

    protected showWorkflowStatusHistory($target: JQuery, history: WorkItemInfo[]): void {
        var columnNames = ['Action', 'StepTitle', 'State', 'ArrivalDate', 'LastModifiedDate', 'SubmitUserDisplayName', 'SubmitDate'];
        var columnMetadata = columnNames
            .map(cn => new rxtable.ColumnInfo(cn, this.columnTitlesByName.getTitle(cn, ViewResultType.WorkTray), (cn === 'Action') ? 0 : 250, (cn === 'Action') ? 'auto' : 'pixel'));

        const runtimeInfo = new RuntimeInfoRequested();
        this.eventAggregator.publish(runtimeInfo);

        var items = history.map(i => this.generateLookupItem(ViewResultType.WorkflowStatusHistory, false, false, i, columnNames, runtimeInfo.value.userId));

        this.insertHistoryTable($target, columnMetadata, items);
    }

    private updatePagerInfo(pageIndex: number, totalItemCount: number) {
        this.pageIndex.next(pageIndex);
        this.totalItemCount.next(totalItemCount);

        var pageSize = this.pageSize.getValue();

        Control.instance($('.wf-page-index .numericbox', this.$element)[0])['content.Value'](totalItemCount > 0 ? pageIndex + 1 : 0);
        $('.wf-total-pages', this.$element).text(Math.ceil(totalItemCount / pageSize));
    }

    protected toLongDateTimeFormat(value: string): string {
        return moment(value, dateTimeTransmissionFormat()).format(longDateTimeFormat(this.resources));
    }

    private initializeView($hostElement: JQuery, resources: IResourceProvider, constructorDisposable: IDisposable): void {
        try {
            this.bulkActionNone = this.resources.getCoreString('XamlNone');
            this.bulkAction.next(this.bulkActionNone);
        } catch (exception) {
            this.eventAggregator.publish(new ScreenLoadError(exception));
            return;
        }

        this.buildViewContainer($hostElement, resources);

        var $table = $('.rxui-table-inner > table', this.$element);

        this.viewScreenDisposable = disposableSubscription(
            this.screenDisposable,
            constructorDisposable,

            this.tHeadDisposable = serialDisposable(null),
            this.tableResizeDisposable = serialDisposable(null),

            rxui.scrolled($table.parent()).switchMap(() => Observable.timer(0)).subscribe(() => rxtable.updateHeadingPositions($table)),
            rxui.from($('window'), 'resize').subscribe(() => rxtable.resizeInternal($table)),

            this.eventAggregator.query(ViewSaveRequested).subscribe(x => this.viewSave(x)),

            this.subscribeToViewActions(),
            this.subscribeToViewPageLoadActions(),
            this.subscribeToViewSubjects()
        );
    }

    private buildViewContainer($hostElement: JQuery, resources: IResourceProvider): void/*Rx.Disposable*/ {
        var viewContainerClass = this.viewScreenType === ViewScreenType.WorkTray ? 'wf-worktray-container'
            : this.viewScreenType === ViewScreenType.WorkflowStatus ? 'wf-workflowstatus-container'
                : /*this.viewScreenType === ViewScreenType.Search ? */'wf-search-container';

        this.$viewTable = $('<div />').addClass('rxui-table-inner wf-view-table-inner').append(
            $('<table />').addClass('rxui-table-fixed').append([
                $('<tbody />'),
                rxtable.createChock()
            ])
        );

        var $pageIndex = $('<div />');
        var $pageSize = $('<div />');

        this.$element = $('<div />').addClass('wf-container').addClass(viewContainerClass).append(
            $('<div />').addClass('wf-outer').append([
                $('<div />').addClass('wf-header'),
                $('<div />').addClass('wf-inner').append([
                    $('<div />').addClass('rxui-table wf-view-table').append([
                        $('<div />').append([
                            $('<div />').addClass('rxui-table-header-background wf-view-table-header'),
                            this.$viewTable
                        ])
                    ])
                ]),
                $('<div />').addClass('wf-footer').append([
                    $('<div />').addClass('wf-pagination').append([
                        $('<div />').addClass('wf-pager').append([
                            $('<span />').addClass('wf-pager-button').addClass('wf-first-page').append([
                                $('<i />').addClass('fa fa-caret-left'),
                                $('<i />').addClass('fa fa-caret-left')
                            ]),
                            $('<span />').addClass('wf-pager-button').addClass('wf-previous-page').append([
                                $('<i />').addClass('fa fa-caret-left')
                            ]),
                            $('<label />').addClass('wf-page-size-label').text(resources.getCoreString('Page')),
                            $('<div />').addClass('wf-page-index').append($pageIndex),
                            $('<label />').text(resources.getCoreString('PageOf')),
                            $('<label />').addClass('wf-total-pages').text(0),
                            $('<span />').addClass('wf-pager-button').addClass('wf-next-page').append([
                                $('<i />').addClass('fa fa-caret-right')
                            ]),
                            $('<span />').addClass('wf-pager-button').addClass('wf-last-page').append([
                                $('<i />').addClass('fa fa-caret-right'),
                                $('<i />').addClass('fa fa-caret-right')
                            ])
                        ])
                    ]),
                    $('<div />').addClass('wf-info').append([
                        $('<div />').addClass('wf-info-left').append([
                            $('<div />').addClass('wf-show-view-menu').append($('<div />').activityButton(this.eventAggregator, resources, { contentText: resources.getCoreString('XamlViews') }))
                        ]),
                        $('<div />').addClass('wf-info-right').append([
                            $('<label />').addClass('wf-page-size-label').text(resources.getCoreString('XamlPageSize')),
                            $('<div />').addClass('wf-page-size').append($pageSize)
                        ])
                    ])
                ])
            ])
            ).appendTo($hostElement).on('remove', () => this.dispose());

        var $pageIndexControl = $pageIndex.numericBox(this.eventAggregator, resources, { contentTextAlignment: 'center', contentValue: 0 });
        var pageIndexControl = <NumericBox>Control.instance($pageIndexControl[0]);
        pageIndexControl.setAdditionalValidator((raw, dec) => raw !== null && dec.greaterThan(0));

        $pageSize.numericBox(this.eventAggregator, resources, { contentTextAlignment: 'center', contentValue: this.defaultPageSize });
    }

    private subscribeToViewActions(): IDisposable {
        return disposableSubscription(rxui.click($('.wf-show-view-menu .activitybutton', this.$element))
            .subscribe(
                mouseEvent => {
                    mouseEvent.stopPropagation();
                    var currentView = this.view.getValue();
                    this.viewMenuShow(mouseEvent.target, currentView.id);
                }
            ));
    }

    private subscribeToViewPageLoadActions(): IDisposable {
        var btnFirstPage = $('.wf-first-page', this.$element);
        var btnPreviousPage = $('.wf-previous-page', this.$element);
        var txtPageIndex = $('.wf-page-index .numericbox', this.$element);
        var btnNextPage = $('.wf-next-page', this.$element);
        var btnLastPage = $('.wf-last-page', this.$element);

        var txtPageSize = $('.wf-page-size .numericbox', this.$element);

        // an empty result set has no last page; without the clamp Next/Last would request page -1
        var lastPageIndex = (pageSize: number, totalItemCount: number): number => Math.max(Math.ceil(totalItemCount / pageSize) - 1, this.firstPageIndex);

        return disposableSubscription(
            rxui.click(btnFirstPage)
                .subscribe(() => this.pageIndex.next(this.firstPageIndex)),
            rxui.click(btnPreviousPage)
                .subscribe(() => this.pageIndex.next(Math.max(this.pageIndex.getValue() - 1, this.firstPageIndex))),
            rxui.on<number>(txtPageIndex, 'content.Value')
                .skipWhile(() => !this.viewInitialized)
                .filter(() => !this.viewUpdating)
                .subscribe(value => this.pageIndex.next(value - 1)),
            rxui.click(btnNextPage)
                .subscribe(() => this.pageIndex.next(Math.min(this.pageIndex.getValue() + 1, lastPageIndex(this.pageSize.getValue(), this.totalItemCount.getValue())))),
            rxui.click(btnLastPage)
                .subscribe(() => this.pageIndex.next(lastPageIndex(this.pageSize.getValue(), this.totalItemCount.getValue()))),

            rxui.on<number>(txtPageSize, 'content.Value')
                .skipWhile(() => !this.viewInitialized)
                .filter(() => !this.viewUpdating)
                .map(value => {
                    if (value === 0) {
                        value = this.defaultPageSize;
                        Control.instance(txtPageSize[0])['content.Value'](value);
                    }
                    return value;
                })
                .distinctUntilChanged()
                .subscribe(value => this.pageSize.next(value))
        );
    }

    private subscribeToViewSubjects(): IDisposable {
        var viewIdSubscription = this.viewId
            .skipWhile(() => !this.viewInitialized)
            .subscribe(
                viewId => {
                    this.updating();

                    this.loadView(viewId, () => this.notUpdating());
                }
            );

        var viewSubscription = this.view
            .skipWhile(() => !this.viewInitialized)
            .subscribe(
                view => {
                    this.updating();
                    this.viewResultType = null;
                    this.sortDescriptions.next(null);
                    this.onViewChanged(view, () => this.notUpdating());
                }
            );

        var sortDescriptionsSubscription = this.sortDescriptions
            .skipWhile(() => !this.viewInitialized)
            .filter(() => !this.viewUpdating)
            .subscribe(
                sortDescriptions => {
                    this.updating();

                    var args: IViewLoadPageArgs = {
                        viewId: this.view.getValue().id,
                        pageSize: this.pageSize.getValue(),
                        pageIndex: this.pageIndex.getValue(),
                        sortDescriptions: sortDescriptions
                    };
                    this.reloadPage(args, () => this.notUpdating());
                }
            );

        var pageSubscription = this.page
            .skipWhile(() => !this.viewInitialized)
            .subscribe(page => this.paintViewBodyVirtual(this.view.getValue(), page));

        return disposableSubscription(
            viewIdSubscription,
            viewSubscription,
            sortDescriptionsSubscription,
            pageSubscription
        );
    }

    private loadView(viewId: string, onCompleted: () => void): void {
        Request.create(this.serializer, path.worktrayView(`${viewId}/${this.viewScreenType}`))
            .handle(WorkTrayView, view => this.view.next(view))
            .handle(SystemError, () => this.systemError('Error in loadView: messages.SystemError'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Error in loadView: ${errorThrown}`, textStatus))
            .final(onCompleted)
            .get();
    }

    private viewMenuShow(element: EventTarget, currentViewId: string): void {
        var onSuccess = (workTrayViewInfos: WorkTrayViewInfo[]): void => {
            var makeViewMenuItem = (id: string, name: string, isSystemView: boolean, isCurrentView: boolean): JQuery => {

                var $nameDiv = $('<div />').addClass('wf-view-menu-item-name');

                const localizedName = isSystemView ? this.resources.getSystemWorkTrayViewString(name) || name : name;
                if (isCurrentView) {
                    $('<strong />').text(localizedName).appendTo($nameDiv);
                } else {
                    $nameDiv.text(localizedName);
                }

                //var $nameDiv = $('<div />').addClass('wf-view-menu-item-name').append($nameSpan);

                var $content = isSystemView
                    ? $('<div />').append([
                        $('<div />').css('float', 'right').append([
                            $('<a />').addClass('wf-view-menu-item-action').data('viewAction', 'view').append([
                                $('<span />').text(this.resources.getCoreString('XamlView'))
                            ]),
                            $('<a />').addClass('wf-view-menu-item-action').data('viewAction', 'clone').append([
                                $('<span />').text(this.resources.getCoreString('XamlClone'))
                            ])
                        ]),
                        $nameDiv
                    ])
                    : $('<div />').append([
                        $('<div />').css('float', 'right').append([
                            $('<a />').addClass('wf-view-menu-item-action').data('viewAction', 'edit').append([
                                $('<span />').text(this.resources.getCoreString('XamlEdit'))
                            ]),
                            $('<a />').addClass('wf-view-menu-item-action').data('viewAction', 'delete').append([
                                $('<span />').text(this.resources.getCoreString('XamlDelete'))
                            ])
                        ]),
                        $nameDiv
                    ]);

                return $('<li />')
                    .addClass('wf-view-menu-item')
                    .addClass('wf-view-menu-item-action')
                    .data({ 'viewAction': 'select', 'viewId': id, 'viewName': name })
                    .append($content);
            };

            var viewInfos = workTrayViewInfos;

            var viewMenuItems = []
                .concat(viewInfos.filter(v => v.isSystem).map(v => (makeViewMenuItem(v.id, v.name, true, (v.id === currentViewId)))))
                .concat(viewInfos.some(v => v.isSystem) && viewInfos.some(v => !v.isSystem) ? [ $('<li />').addClass('wf-view-menu-divider').text('-')] : [])
                .concat(viewInfos.filter(v => !v.isSystem).map(v => (makeViewMenuItem(v.id, v.name, false, (v.id === currentViewId)))))
                .concat([
                    $('<li />').addClass('wf-view-menu-divider').text('-'),
                    $('<li />').addClass('wf-view-menu-item').addClass('wf-view-menu-item-action').data('viewAction', 'new').append(
                        $('<div />').addClass('wf-view-menu-item-name').text(this.resources.getCoreString('LblCreateNewView'))
                    )
                ]);

            var viewMenu = $('<div />')
                .addClass('wf-view-menu')
                .append(viewMenuItems.reduce(($ul, $li) => ($ul.append($li)), $('<ul />')))
                .appendTo(this.$element.closest('.runtime-application-container'))
                .position({ my: 'left bottom', at: 'left top', of: element });

            var viewMenuSubscription = rxui.click($('.wf-view-menu-item', viewMenu))
                .subscribe(
                    mouseEvent => {
                        mouseEvent.stopPropagation();

                        var $target = $(mouseEvent.target);

                        var $actionElement = $target.closest('.wf-view-menu-item-action');
                        var action: string = $actionElement.data('viewAction');

                        if (action === 'new') {
                            this.viewNew();
                        } else {
                            var $menuElement = $actionElement.closest('.wf-view-menu-item');

                            var viewId: string = $menuElement.data('viewId');
                            var viewName: string = $menuElement.data('viewName');

                            switch (action) {
                                case 'select':
                                    this.viewSelect(viewId);
                                    break;
                                case 'edit':
                                    this.viewEdit(viewId, viewName);
                                    break;
                                case 'delete':
                                    this.viewDelete(viewId, viewName);
                                    break;
                                case 'view':
                                    this.viewView(viewId, viewName);
                                    break;
                                case 'clone':
                                    this.viewClone(viewId, viewName);
                                    break;
                                default:
                                    alert('unknown view action');
                                    break;
                            };
                        }

                        this.viewMenuDisposable.set(null);
                    }
                );

            this.viewMenuDisposable.set(disposableSubscription(
                viewMenuSubscription,
                disposable(() => viewMenu.remove()),
                rxui.clickOutside(viewMenu).merge(rxui.windowResized()).subscribe(() => this.viewMenuDisposable.set(null))
            ));
        };

        Request.create(this.serializer, path.worktrayView('list'))
            .before(() => this.updating())
            .handleArray(WorkTrayViewInfo, onSuccess)
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError('Error getting list of views.', textStatus))
            .final(() => this.notUpdating())
            .get();
    }

    private viewNew(): void {
        Request.create(this.serializer, path.worktrayView('new'))
            .before(() => {
                this.pauseBackgroundProcessing();
                this.eventAggregator.publish(new Busy());
            })
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .handle(WorkTrayView, view => this.eventAggregator.publish(new DialogRequested(new ViewEditDialog(this.eventAggregator, view, true, this.resources, this.columnTitlesByName, () => this.resumeBackgroundProcessing()))))
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(null, textStatus))
            .get();
    }

    private viewSelect(viewId: string): void {
        this.viewId.next(viewId);
    }

    private viewEdit(viewId: string, viewName: string): void {
        Request.create(this.serializer, path.worktrayView(`${viewId}`))
            .before(() => {
                this.pauseBackgroundProcessing();
                this.eventAggregator.publish(new Busy());
            })
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .handle(null, () => this.eventAggregator.publish(new DialogRequested(new WorkTrayViewNotFoundDialog(viewName, this.resources))))
            .handle(WorkTrayView, view => this.eventAggregator.publish(new DialogRequested(new ViewEditDialog(this.eventAggregator, view, false, this.resources, this.columnTitlesByName, () => this.resumeBackgroundProcessing()))))
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(null, textStatus))
            .get();
    }

    // Open system view in read-only mode
    private viewView(viewId: string, viewName: string): void {
        Request.create(this.serializer, path.worktrayView(`${viewId}`))
            .before(() => {
                this.pauseBackgroundProcessing();
                this.eventAggregator.publish(new Busy());
            })
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .handle(null, () => this.eventAggregator.publish(new DialogRequested(new WorkTrayViewNotFoundDialog(viewName, this.resources))))
            .handle(WorkTrayView, view => this.eventAggregator.publish(new DialogRequested(new ViewEditDialog(this.eventAggregator, view, false, this.resources, this.columnTitlesByName, () => this.resumeBackgroundProcessing(), true))))
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(null, textStatus))
            .get();
    }

    // Clone system view: pre-populate dialog as new view editable
    private viewClone(viewId: string, viewName: string): void {
        Request.create(this.serializer, path.worktrayView(`${viewId}`))
            .before(() => {
                this.pauseBackgroundProcessing();
                this.eventAggregator.publish(new Busy());
            })
            .final(() => this.eventAggregator.publish(new NotBusy()))
            .handle(null, () => this.eventAggregator.publish(new DialogRequested(new WorkTrayViewNotFoundDialog(viewName, this.resources))))
            .handle(WorkTrayView, view => {
                // treat as new
                view.id = newGuid(); // ensure it's saved as a new user view
                this.eventAggregator.publish(new DialogRequested(new ViewEditDialog(this.eventAggregator, view, true, this.resources, this.columnTitlesByName, () => this.resumeBackgroundProcessing())));
            })
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(null, textStatus))
            .get();
    }

    private viewDelete(viewId: string, viewName: string): void {

        var action = (dialog: JQuery) => {
            this.updating();

            var onCompleted = (): void => {
                this.notUpdating();

                dialog.dialog('close');
            };

            Request.create(this.serializer, path.worktrayView(`${viewId}/delete`))
                .handle(null, () => {
                    onCompleted();
                    var currentView = this.view.getValue();
                    if (viewId === currentView.id) {
                        this.viewId.next(emptyGuid); //setting to empty instead of null to prevent serialization issues
                    }
                })
                .handle(WorkTrayViewNotFound, () => {
                    onCompleted();
                    this.eventAggregator.publish(new DialogRequested(new WorkTrayViewNotFoundDialog(viewName, this.resources)));
                })
                .handle(SystemError, () => (onCompleted(), alert('Failed to delete view.')))
                .error((jqXhr, textStatus, errorThrown) => (onCompleted(), this.httpError(errorThrown, textStatus)))
                .post(this.csrfToken());
        };

        this.eventAggregator.publish(new DialogRequested(new ConfirmWorkTrayViewDeletionDialog(viewName, this.resources, action)));
    }

    private viewSave(request: ViewSaveRequested): void {

        Request.create(this.serializer, path.worktrayView(`${request.view.id}/save`))
            .args(() => request.view)
            .before(() => this.updating())
            .handle(null, () => {
                request.completionAction();
                this.viewId.next(request.view.id);
            })
            .handle(SystemError, () => this.systemError('Failed to save view settings.'))
            .handle(ValidationFailed, validationFailed => request.validationFailed(validationFailed.validationSummary))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Failed to save Work Tray View: ${errorThrown}`, textStatus))
            .final(() => this.notUpdating())
            .post(this.csrfToken());
    }

    private determineViewColumnsInOrder(view: WorkTrayView, viewResultType: ViewResultType): ViewColumn[] {
        var columnsToExclude = this.columnExcludesByViewResultType.get(viewResultType);
        return view.columns
            .filter(c => c.isVisible && columnsToExclude.indexOf(c.name) === -1)
            .sort((a, b) => a.orderSequence - b.orderSequence)
            .map(c => {
                return (c.name === 'Action' && this.bulkAction.getValue() !== this.bulkActionNone)
                    ? new ViewColumn('Process', '')
                    : new ViewColumn(c.name, c.displayName);
            });
    }

    private applyColumnResizingLogic($table: JQuery): void {
        this.tableResizeDisposable.set(null);

        this.tableResizeDisposable.set(disposableSubscription(
            rxtable.makeColumnsResizable($table).subscribe(value => {
                var column = this.columns[value.index];
                var columnSizeInfo = this.columnSizeInfos.filter(c => c.name === column.name)[0];
                if (columnSizeInfo === undefined || columnSizeInfo === null) {
                    columnSizeInfo = {
                        name: column.name,
                        sizeType: 'pixel',
                        size: Math.max(value.size, this.minColumnWidth)
                    };
                    this.columnSizeInfos.push(columnSizeInfo);
                } else {
                    columnSizeInfo.sizeType = 'pixel';
                    columnSizeInfo.size = Math.max(value.size, this.minColumnWidth);
                }

                this.columnMetadata = this.getColumnInfos(this.columns, this.columnSizeInfos);

                rxtable.resize($table, this.columnMetadata, this.minColumnWidth, this.autoClearance);
            })
        ));
    }

    private moveColumn(oldIndex: number, newIndex: number): void {
        if (oldIndex === newIndex) {
            return;
        }

        var $table = $('.wf-view-table-inner > table', this.$element);

        rxtable.moveColumn($table, oldIndex, newIndex, this.columns.length);

        // this prevents placing columns after the filler
        if (newIndex === this.columns.length) {
            newIndex--;
        }

        this.columns.splice(newIndex, 0, this.columns.splice(oldIndex, 1)[0]);
        this.columnMetadata = this.getColumnInfos(this.columns, this.columnSizeInfos);

        this.applyColumnResizingLogic($table);
    }

    private sortColumn(sortRequest: rxtable.ColumnSortRequest): void {
        if (sortRequest.index < 0) {
            return;
        }
        if (sortRequest.index >= this.columns.length) {
            return;
        }
        var column = this.columns[sortRequest.index];

        var currentSortDescs = this.sortDescriptions.getValue() || [];

        var sortDescs = [new SortDescription(column.name, sortRequest.ascending)].concat(currentSortDescs.filter(x => x.columnName !== column.name));

        this.sortDescriptions.next(sortDescs);
    }
}

export interface IColumnSizeInfo {
    name: string;
    size: number;
    sizeType: string;
}

export class ColumnTitlesByName {
    private map = Map<string, string | Map<ViewResultType, string>>().asMutable();

    public set(columnName: string, value: string | Map<ViewResultType, string>) {
        this.map.set(columnName, value);
    }

    public getTitle(name: string, viewResultType: ViewResultType): string {
        const titleItem = this.map.get(name);

        return Map.isMap(titleItem)
            ? (<Map<ViewResultType, string>>titleItem).get(viewResultType)
            : <string>titleItem;
    }
}